import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import { useLang } from "../components/LangContext";
import { STR } from "../lib/i18n";
import { api, API } from "../lib/api";
import { CATEGORY_FALLBACK_IMG, formatDate } from "../lib/categories";

export default function Reportage() {
  const { lang } = useLang();
  const t = STR[lang];
  const TABS = [
    { slug: "alla", label: t.list.all },
    { slug: "fiske", label: t.categories.fiske.label },
    { slug: "skapande", label: t.categories.skapande.label },
    { slug: "ovrigt", label: t.categories.ovrigt.label },
  ];
  const [searchParams, setSearchParams] = useSearchParams();
  const active = searchParams.get("kategori") || "alla";
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = active === "alla" ? { lang } : { category: active, lang };
    api
      .get("/posts", { params })
      .then((r) => setPosts(r.data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, [active, lang]);

  return (
    <div data-testid="reportage-page" className="min-h-screen bg-[#F6F5F0]">
      <Navbar />
      <div className="mx-auto max-w-7xl px-6 pb-24 pt-36 md:px-12 md:pt-44">
        <Reveal y={24}>
          <p className="overline-label text-[#9E643E]">{t.list.overline}</p>
          <h1 className="mt-6 font-serif text-4xl font-medium tracking-tight text-[#1A221E] sm:text-5xl lg:text-6xl">
            {t.list.title}
          </h1>
        </Reveal>

        <div data-testid="category-tabs" className="mt-12 flex flex-wrap gap-3 border-b border-[#D8D3C4] pb-8">
          {TABS.map((tab) => (
            <button
              key={tab.slug}
              data-testid={`tab-${tab.slug}`}
              onClick={() => setSearchParams(tab.slug === "alla" ? {} : { kategori: tab.slug })}
              className={`rounded-full px-6 py-2.5 text-sm font-semibold tracking-wide transition-colors duration-300 ${
                active === tab.slug
                  ? "bg-[#2D4032] text-[#F6F5F0]"
                  : "border border-[#D8D3C4] text-[#2D4032] hover:border-[#2D4032]"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p data-testid="reportage-loading" className="mt-20 font-serif text-2xl italic text-[#7B8A80]">
            {t.list.loading}
          </p>
        ) : posts.length === 0 ? (
          <div data-testid="reportage-empty" className="mt-20 max-w-md">
            <p className="font-serif text-3xl italic text-[#2D4032]">{t.list.emptyTitle}</p>
            <p className="mt-4 text-base text-[#1A221E]/70">{t.list.emptyBody}</p>
          </div>
        ) : (
          <div className="mt-16 grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-3">
            {posts.map((post, i) => (
              <Reveal key={post.id} delay={0.06 * (i % 3)}>
                <Link to={`/reportage/${post.slug}`} data-testid={`post-card-${post.slug}`} className="group block">
                  <div className="overflow-hidden">
                    <img
                      src={post.images?.[0] ? `${API}/files/${post.images[0]}` : CATEGORY_FALLBACK_IMG[post.category]}
                      alt={post.title}
                      loading="lazy"
                      className="aspect-[4/5] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  </div>
                  <p className="overline-label mt-6 text-[#9E643E]">{t.categories[post.category].label}</p>
                  <h2 className="mt-3 font-serif text-2xl font-medium tracking-tight text-[#1A221E] transition-colors duration-300 group-hover:text-[#9E643E]">
                    {post.title}
                  </h2>
                  <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-[#1A221E]/70">{post.excerpt}</p>
                  <p className="mt-4 text-xs tracking-widest text-[#7B8A80]">{formatDate(post.created_at, lang)}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
